import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Modal from '@mui/material/Modal';
import '../TestingReport/TestingReport.scss';
import { Box, Tabs, Tab, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import Button from 'src/component/UI/Button';
import { getDeviceById } from 'src/module/device/selector';
import FinalizeAndSignOff from 'src/screen/Protected/DeviceScreen/TestingReport/FinalizeAndSignOff';
import { Map, fromJS } from 'immutable';
import { getAbilities } from 'src/module/authentication/selector';
import { fetchCheckSheet, saveCheckSheet, updateCheckSheet } from 'src/module/checkSheet/action';
import { getCheckSheetReport, getSaveCheckSheetReportLoading } from 'src/module/checkSheet/selector';
import useMediaQuery from '@mui/material/useMediaQuery';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';
import { pairUp } from 'src/screen/Protected/DeviceScreen/Commissioning/utils';

const CHECK_SHEET_TYPE = 'cable-repair-splice';
const LAST_TAB = '6';

export default function SpliceRepairReport (props) {
  const {
    deviceId,
    reportId,
    open
  } = props;

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState('1');
  const report = useSelector(getCheckSheetReport(reportId));
  const device = useSelector(getDeviceById(deviceId));
  const isSaveLoading = useSelector(getSaveCheckSheetReportLoading(CHECK_SHEET_TYPE));
  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);
  const abilities: any = useSelector(getAbilities);
  const isPhoneView = useMediaQuery(`(max-width: ${variables.phoneWidth})`);

  const canEdit = abilities.includes('edit-check-sheet-report');
  const readOnly = Boolean(reportId) && !canEdit;

  const buildEmptyResults = () => Map({
    info: {
      type: CHECK_SHEET_TYPE,
      deviceId,
    },
    extras: fromJS({
      circuitInfo: {
        ehtTagNo: device?.get('tag') || '',
        controlPanelNo: '',
        htcNo: '',
        distributionPanelNo: device?.get('distribution_panel') || '',
        cbNo: '',
        ehtIsometricRev: '',
      },
      cableTestingPrior: {
        cableTag: '',
        cableMfq: '',
        cablePartNo: '',
        resistance: '',
        ir: '',
      },
      pointOfFailure: {
        location: '',
        spliceCompleted: '',
        materialUsed: '',
        additionalNotes: '',
      },
      cableTestingAfter: {
        waterTestResult: '',
        cableResistance: '',
        cableIr: '',
        circuitVoltage: '',
        circuitCurrent: '',
      },
      testEquipment: {
        irMeterId: '',
        irCalDate: '',
        irMeterMfq: '',
        multimeterId: '',
        multimeterCalDate: '',
        multimeterMfq: '',
      },
    }),
    signOff: Map({
      recommendations: '',
    }),
    checkSheet: fromJS([]),
  });

  const [allResults, setAllResults] = useState(reportId === null ? buildEmptyResults() : null);

  useEffect(() => {
    if (open && reportId) {
      dispatch(fetchCheckSheet(reportId, CHECK_SHEET_TYPE));
    } else if (open && !reportId) {
      setAllResults(buildEmptyResults());
      setTabValue('1');
    }
  }, [reportId, open]);

  useEffect(() => {
    if (reportId && report.get('extras')) {
      setAllResults(Map(report));
    }
  }, [report, reportId]);

  if (!open) {
    return null;
  }

  if (reportId && !report.get('extras')) {
    return null;
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const next = () => setTabValue((parseInt(tabValue) + 1).toString());
  const previous = () => setTabValue((parseInt(tabValue) - 1).toString());

  const change = (path: string[], value: any) => {
    setAllResults(prev => prev.setIn(path, value));
  };

  const submit = () => {
    if (reportId) {
      dispatch(updateCheckSheet(allResults, deviceId, CHECK_SHEET_TYPE));
    } else {
      dispatch(saveCheckSheet(CHECK_SHEET_TYPE, allResults, deviceId));
    }
  };

  const extras: any = allResults?.get('extras');

  const DATE_FIELDS = new Set(['irCalDate', 'multimeterCalDate']);

  const renderField = (section: string, key: string, disabled = false, multiline = false) => {
    if (DATE_FIELDS.has(key)) {
      const rawValue = extras?.getIn([section, key]);
      const dateValue = rawValue ? moment(rawValue, 'YYYY-MM-DD') : null;
      return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            disabled={readOnly || disabled}
            value={dateValue}
            onChange={(date: moment.Moment | null) => change(['extras', section, key], date ? date.format('YYYY-MM-DD') : '')}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </LocalizationProvider>
      );
    }
    return (
      <TextField
        fullWidth
        disabled={readOnly || disabled}
        value={extras?.getIn([section, key]) ?? ''}
        multiline={multiline}
        rows={multiline ? 3 : 1}
        onChange={(e) => change(['extras', section, key], e.target.value)}
      />
    );
  };

  const renderTable = (section: string, fields: string[], extraRows?: React.ReactNode) => {
    const pairs = pairUp(fields);
    return (
      <TableContainer className={`default-control-panel-table${isMobile ? ' mobile-table' : ''}`}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className='sub-headers'>{t('common.field')}</TableCell>
              <TableCell className='sub-headers'>{t('common.value')}</TableCell>
              <TableCell className='sub-headers'>{t('common.field')}</TableCell>
              <TableCell className='sub-headers'>{t('common.value')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pairs.map(([fieldA, fieldB]) => (
              <TableRow key={fieldA}>
                <TableCell className='sub-headers'>{t(`spliceRepairReport.field.${fieldA}`)}</TableCell>
                <TableCell className='body-cell'>{renderField(section, fieldA)}</TableCell>
                {fieldB ? (
                  <>
                    <TableCell className='sub-headers'>{t(`spliceRepairReport.field.${fieldB}`)}</TableCell>
                    <TableCell className='body-cell'>{renderField(section, fieldB)}</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell />
                    <TableCell />
                  </>
                )}
              </TableRow>
            ))}
            {extraRows}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const nextButton = () => <Button className='next-button' onClick={next} cta>{t('common.next')}</Button>;
  const submitButton = () => (
    <Button onClick={submit} disabled={readOnly} loading={isSaveLoading} cta>
      {reportId ? t('common.save') : t('common.submit')}
    </Button>
  );

  return (
    <Modal open={props.open} onClose={props.handleClose} style={{ height: '90vh' }}>
      <div className={`modal testing-report-modal${isPhoneView ? ' modal-no-padding-sides' : ''}`}>
        {!isMobile ? <h2>{t('spliceRepairReport.title')}<b> ({device.get('tag')})</b></h2> : null}
        <Box sx={{ width: '100%', marginBottom: '20px' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            textColor='primary'
            indicatorColor='primary'
            aria-label="splice-repair-report-tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab value="1" label={<span className='tab-label-size'>{t('spliceRepairReport.circuitInfo')}</span>} />
            <Tab value="2" label={<span className='tab-label-size'>{t('spliceRepairReport.priorTesting')}</span>} />
            <Tab value="3" label={<span className='tab-label-size'>{t('spliceRepairReport.pointOfFailure')}</span>} />
            <Tab value="4" label={<span className='tab-label-size'>{t('spliceRepairReport.afterTesting')}</span>} />
            <Tab value="5" label={<span className='tab-label-size'>{t('spliceRepairReport.testEquipment')}</span>} />
            <Tab value="6" label={<span className='tab-label-size'>{t('spliceRepairReport.signOff')}</span>} />
          </Tabs>
        </Box>

        {tabValue === '1' && (
          <Paper sx={{ marginBottom: '30px' }}>
            <h3>{t('spliceRepairReport.circuitInfo')}</h3>
            {renderTable('circuitInfo', ['ehtTagNo', 'controlPanelNo', 'htcNo', 'distributionPanelNo', 'cbNo', 'ehtIsometricRev'])}
          </Paper>
        )}

        {tabValue === '2' && (
          <Paper sx={{ marginBottom: '30px' }}>
            <h3>{t('spliceRepairReport.priorTesting')}</h3>
            {renderTable('cableTestingPrior', ['cableTag', 'cableMfq', 'cablePartNo', 'resistance', 'ir'])}
          </Paper>
        )}

        {tabValue === '3' && (
          <Paper sx={{ marginBottom: '30px' }}>
            <h3>{t('spliceRepairReport.pointOfFailure')}</h3>
            {renderTable('pointOfFailure', ['location', 'spliceCompleted', 'materialUsed'],
              <TableRow>
                <TableCell className='sub-headers'>{t('spliceRepairReport.field.additionalNotes')}</TableCell>
                <TableCell className='body-cell' colSpan={3}>{renderField('pointOfFailure', 'additionalNotes', false, true)}</TableCell>
              </TableRow>
            )}
          </Paper>
        )}

        {tabValue === '4' && (
          <Paper sx={{ marginBottom: '30px' }}>
            <h3>{t('spliceRepairReport.afterTesting')}</h3>
            {renderTable('cableTestingAfter', ['waterTestResult', 'cableResistance', 'cableIr', 'circuitVoltage', 'circuitCurrent'])}
          </Paper>
        )}

        {tabValue === '5' && (
          <Paper sx={{ marginBottom: '30px' }}>
            <h3>{t('spliceRepairReport.testEquipment')}</h3>
            {renderTable('testEquipment', ['irMeterId', 'irCalDate', 'irMeterMfq', 'multimeterId', 'multimeterCalDate', 'multimeterMfq'])}
          </Paper>
        )}

        {tabValue === '6' && (
          <FinalizeAndSignOff readOnly={readOnly} data={allResults?.get('signOff')} onChange={change} />
        )}

        <div className='button-bar stay-at-bottom'>
          <Button onClick={() => props.handleClose()}>{t('common.cancel')}</Button>
          {tabValue !== '1' ? <Button onClick={previous}>{t('common.previous')}</Button> : null}
          {tabValue === LAST_TAB ? submitButton() : nextButton()}
        </div>
      </div>
    </Modal>
  );
}

SpliceRepairReport.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  deviceId: PropTypes.string,
  reportId: PropTypes.string,
};
